import { inject, Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { BusinessUnitService } from '@app/api/user-service/api/businessUnit.service';
import { RoleManagementService } from '@app/api/user-service/api/roleManagement.service';
import { UserBusinessUnitsService } from '@app/api/user-service/api/userBusinessUnits.service';
import type { BusinessUnitSearchRequest } from '@app/api/user-service/model/businessUnitSearchRequest';
import type { BusinessUnitTreeNode } from '@app/api/user-service/model/businessUnitTreeNode';
import type { UserBusinessUnitAssignmentItem } from '@app/api/user-service/model/userBusinessUnitAssignmentItem';
import type { UserBusinessUnitBulkAssignRequest } from '@app/api/user-service/model/userBusinessUnitBulkAssignRequest';

export interface RolesPageResult {
  totalElements: number;
  pageContent: Record<string, unknown>[];
}

/**
 * Feature-level facade over user/roles/BU APIs. Use this instead of
 * calling API services directly from components.
 */
@Injectable({ providedIn: 'root' })
export class EmployeeMgmtService {
  private readonly roleApi = inject(RoleManagementService);
  private readonly businessUnitApi = inject(BusinessUnitService);
  private readonly userBusinessUnitsApi = inject(UserBusinessUnitsService);

  /** Role options for search form dropdown (label = name, value = name). */
  getRoleOptions(): Observable<{ label: string; value: string }[]> {
    return this.getRolesPage(0, 500).pipe(
      map((page) =>
        page.pageContent.map((r: Record<string, unknown>) => ({
          label: (r['name'] ?? r['id'] ?? '') as string,
          value: (r['name'] ?? r['id'] ?? '') as string,
        })),
      ),
      catchError(() => of([])),
    );
  }

  /** Business unit options for search form dropdown. */
  getBusinessUnitSearchOptions(): Observable<
    { label: string; value: number }[]
  > {
    return this.businessUnitApi
      .search({ size: 500 } as BusinessUnitSearchRequest, 'body', false)
      .pipe(
        switchMap((body: unknown) => this.parseJsonBody(body)),
        map((page) => {
          const content = page['pageContent'] ?? page['pagecontent'];
          const arr = Array.isArray(content) ? content : [];
          return arr.map((bu: Record<string, unknown>) => ({
            label: (bu['name'] ?? bu['code'] ?? bu['id'] ?? '') as string,
            value: (bu['id'] ?? 0) as number,
          }));
        }),
        catchError(() => of([])),
      );
  }

  getRolesPage(
    page: number,
    size: number,
    search?: string,
  ): Observable<RolesPageResult> {
    return this.roleApi.getRoles(page, size, search, 'body', false).pipe(
      switchMap((body: unknown) => this.parseJsonBody(body)),
      map((raw) => {
        const content = raw['pageContent'] ?? raw['pagecontent'];
        const arr = Array.isArray(content) ? content : [];
        const total = raw['totalElements'] ?? raw['totalelements'];
        return {
          totalElements: typeof total === 'number' ? total : arr.length,
          pageContent: arr as Record<string, unknown>[],
        };
      }),
      catchError(() => of({ totalElements: 0, pageContent: [] })),
    );
  }

  getBusinessUnitTree(): Observable<BusinessUnitTreeNode[]> {
    return this.businessUnitApi.getBusinessUnitTree('body', false).pipe(
      switchMap((body: unknown) => {
        if (body instanceof Blob && body.type?.includes('json')) {
          return from(body.text()).pipe(
            map((text) => JSON.parse(text) as BusinessUnitTreeNode[]),
          );
        }
        return of((body ?? []) as BusinessUnitTreeNode[]);
      }),
      catchError(() => of([])),
    );
  }

  getAssignmentsForUser(
    userId: string,
  ): Observable<{ assignments: UserBusinessUnitAssignmentItem[] }> {
    return this.userBusinessUnitsApi
      .getAssignmentsForUser(userId, undefined, 'body', false)
      .pipe(
        switchMap((body: unknown) => {
          if (body instanceof Blob && body.type?.includes('json')) {
            return from(body.text()).pipe(
              map(
                (t) =>
                  JSON.parse(t) as {
                    assignments?: UserBusinessUnitAssignmentItem[];
                  },
              ),
            );
          }
          return of(
            (body ?? {}) as { assignments?: UserBusinessUnitAssignmentItem[] },
          );
        }),
        catchError(() => of({ assignments: [] })),
        map((res) => ({ assignments: res.assignments ?? [] })),
      );
  }

  assignUsersToBusinessUnitBulk(
    request: UserBusinessUnitBulkAssignRequest,
  ): Observable<unknown> {
    const body = this.toSerializableBulkRequest(request);
    return this.userBusinessUnitsApi.assignUsersToBusinessUnitBulk(
      body as unknown as UserBusinessUnitBulkAssignRequest,
      'body',
      false,
    );
  }

  unassignUsersFromBusinessUnitBulk(
    request: UserBusinessUnitBulkAssignRequest,
  ): Observable<unknown> {
    const body = this.toSerializableBulkRequest(request);
    return this.userBusinessUnitsApi.unassignUsersFromBusinessUnitBulk(
      body as unknown as UserBusinessUnitBulkAssignRequest,
      'body',
      false,
    );
  }

  /** Converts Set fields to arrays so JSON.stringify sends correct payload. */
  private toSerializableBulkRequest(
    request: UserBusinessUnitBulkAssignRequest,
  ): Record<string, unknown> {
    return {
      businessUnitId: request.businessUnitId,
      role: request.role,
      userIds: request.userIds ? Array.from(request.userIds) : undefined,
      emails: request.emails ? Array.from(request.emails) : undefined,
    };
  }

  private parseJsonBody(body: unknown): Observable<Record<string, unknown>> {
    if (body instanceof Blob && body.type?.includes('json')) {
      return from(body.text()).pipe(
        map((text) => JSON.parse(text) as Record<string, unknown>),
      );
    }
    return of((body ?? {}) as Record<string, unknown>);
  }
}
